import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Card, Reveal, PullQuote } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { SERVICE_LINES } from '../data/services';
export function ServicesOverview() {
  return <>
      <section className="surface-dark" data-id="element-1377" data-mp="cvvebahpih9m">
        <Container className="py-16 sm:py-24" data-id="element-1378" data-mp="gvvtixcmqwhq">
          <Kicker data-id="element-1379" data-mp="gv482fcqyf56">Services</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-1380" data-mp="lu6zuy3puov7">
            Three lines, one method, in the order the work usually needs
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-1381" data-mp="vsmv9d9s0kai">
            Intelligence establishes where the value is. Growth builds the route to it. AI makes the whole thing
            repeatable without adding headcount. You can start anywhere. Most clients start with Intelligence.
          </p>
        </Container>
      </section>

      <Section data-id="element-1382" data-mp="b0k5hg6yrx5g">
        <div className="grid gap-6 lg:grid-cols-3" data-id="element-1383" data-mp="b1d6s1n5lug7">
          {SERVICE_LINES.map((line, index) => <Reveal key={line.id} delay={index * 0.06} data-id="element-1384" data-mp="67qzyq3o8ksb" data-mpi={index}>
              <Card className="flex h-full flex-col" data-id="element-1385" data-mp="197d8nh974yw">
                <span className="font-display text-[13px] text-gold" data-id="element-1386" data-mp="17wc6vf70bbh">Step 0{index + 1}</span>
                <h2 className="mt-3 font-display text-[26px] leading-tight text-ink" data-id="element-1387" data-mp="3se74a22861qo">{line.name}</h2>
                <p className="mt-3 text-[15px] text-body" data-id="element-1388" data-mp="twjzsxnezcs7">{line.promise}</p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1389" data-mp="p0uu0ckswzng">Who it is for</p>
                <p className="mt-2 text-[14px] text-body" data-id="element-1390" data-mp="jyni1mzz1jl4">{line.who}</p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1391" data-mp="fy3i6bo0k1va">The outcome</p>
                <p className="mt-2 text-[14px] text-body" data-id="element-1392" data-mp="34s8y8cndy6wv">{line.outcome}</p>
                <Link to={`/services/${line.id}`} className="mt-auto inline-flex items-center gap-2 pt-7 text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-1393" data-mp="30o18qd93smxl">
                  Explore this line
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1394" data-mp="2s8c1c7pv09ic" />
                </Link>
              </Card>
            </Reveal>)}
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="public-delivery" data-id="element-1395" data-mp="2x8zqsoyfwiag">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center" data-id="element-1396" data-mp="2je7518ryi5vf">
          <div data-id="element-1397" data-mp="2o7jqxb593ykh">
            <Kicker data-id="element-1398" data-mp="2o81m04g6g6qt">For government</Kicker>
            <h2 id="public-delivery" className="mt-4 font-display text-[28px] leading-tight text-ink sm:text-[36px]" data-id="element-1399" data-mp="29hm16qrob0t6">
              Cavalla Public Delivery
            </h2>
            <p className="mt-5 max-w-xl text-[17px] text-body" data-id="element-1400" data-mp="2eekrey87unm3">
              A dedicated instrument for governments implementing a national development plan: one agreed
              baseline, each programme budget line joined to the outcome it is meant to move, and an assisted
              early warning on slippage. Our pilot is scoped against Liberia&rsquo;s ARREST Agenda for Inclusive
              Development, 2025 to 2029.
            </p>
          </div>
          <div className="lg:justify-self-end" data-id="element-1401" data-mp="1uvyasnuvn01v">
            <Button to="/government" size="lg" shape="pill" data-id="element-1402" data-mp="1usiy1rooe33m">
              See the pilot programme
            </Button>
          </div>
        </div>
      </Section>

      <Section tone="mist" data-id="element-1403" data-mp="24ri6pa9fxyqm">
        <div className="grid gap-12 lg:grid-cols-2" data-id="element-1404" data-mp="24qqapybxlyzt">
          <SectionHeader kicker="How they connect" title="The path to depth" lead="Each line stands alone. Together they compound, because the evidence gathered in one becomes the input to the next." data-id="element-1405" data-mp="1zsx81f9u7n4a" />
          <div className="space-y-6" data-id="element-1406" data-mp="29m5n0luajmu4">
            <PullQuote data-id="element-1407" data-mp="2nirjbseid3mf">
              A market study that never becomes a plan is an expensive opinion. A plan without evidence is a
              guess with a deadline.
            </PullQuote>
            <p className="text-[15px] text-body" data-id="element-1408" data-mp="32ndse6dwofaf">
              We built the three lines so a client can begin with the question they actually have, rather than the
              engagement a firm wants to sell. If you know your market and need demand, start with Growth. If you
              are being pressed on AI and cannot tell hype from opportunity, start with AI.
            </p>
            <Button to="/augmented-intelligence" variant="secondary" data-id="element-1409" data-mp="2jnzwvk0j2wht">
              Read our method
            </Button>
          </div>
        </div>
      </Section>

      <section className="surface-dark" data-id="element-1410" data-mp="2nrec48gn78u7">
        <Container className="py-16" data-id="element-1411" data-mp="2snv9zudn91kh">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center" data-id="element-1412" data-mp="2smtosx2wyyp1">
            <h2 className="max-w-xl font-display text-[28px] leading-tight text-white" data-id="element-1413" data-mp="2xosqm2j09njv">
              Not certain which line fits your question
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row" data-id="element-1414" data-mp="cwh5dt75dsaj">
              <Button to="/contact" size="lg" data-id="element-1415" data-mp="cz3alm6kfvpl">Book a discovery call</Button>
              <Button to="/assessment" variant="reversed" size="lg" data-id="element-1416" data-mp="1xej8xm54xyxc">Take the assessment</Button>
            </div>
          </div>
        </Container>
      </section>
    </>;
}